import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { BookOpen, BookMarked, MessageCircleQuestion, Loader2 } from "lucide-react";

/* Floating @-mention picker: scripture (chapters/verses) + questions. */
export const MentionMenu = forwardRef(function MentionMenu({ items, loading, query, clientRect, command }, ref) {
  const [index, setIndex] = useState(0);
  const listRef = useRef(null);
  const commentary = items.commentary || [];
  const flat = [...items.scriptures.map((s) => ({ ...s, group: "scripture" })), ...items.questions.map((q) => ({ ...q, group: "question" })), ...commentary.map((c) => ({ ...c, group: "commentary" }))];

  useEffect(() => { setIndex(0); }, [query, items]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  const pick = (item) => {
    if (!item) return;
    command({ id: item.kind === "question" || item.kind === "commentary" ? item.id : item.label, label: item.label, kind: item.kind, href: item.href });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") { setIndex((i) => (flat.length ? (i + 1) % flat.length : 0)); return true; }
      if (event.key === "ArrowUp") { setIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0)); return true; }
      if (event.key === "Enter" || event.key === "Tab") { if (flat[index]) { pick(flat[index]); return true; } return false; }
      return false;
    },
  }));

  const rect = clientRect?.();
  if (!rect) return null;
  const width = 420;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = rect.bottom + 8;
  const style = below + 340 > window.innerHeight && rect.top > 340
    ? { position: "fixed", left, bottom: window.innerHeight - rect.top + 8, width }
    : { position: "fixed", left, top: below, width };

  let i = -1;
  const Section = ({ title, icon: Icon, rows }) => rows.length === 0 ? null : (
    <div>
      <p className="flex items-center gap-1.5 px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3 w-3 text-gold" /> {title}</p>
      {rows.map((it) => {
        i += 1;
        const idx = i;
        return (
          <button
            key={`${it.kind}-${it.label}-${it.id || ""}`}
            type="button"
            data-index={idx}
            data-testid={`mention-item-${idx}`}
            onMouseDown={(e) => { e.preventDefault(); pick(it); }}
            onMouseEnter={() => setIndex(idx)}
            className={`flex w-full items-start gap-3 px-3 py-2 text-left transition-colors ${idx === index ? "bg-gold/15" : "hover:bg-muted"}`}
          >
            <span className="no-reftag mt-0.5 shrink-0 font-mono text-xs text-foreground">{it.label}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{it.kind === "question" ? <i className="font-serif text-foreground/90">“{it.detail}”</i> : it.detail}</span>
            {it.question_id && <span className="mt-0.5 shrink-0 rounded-full border border-gold/40 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-gold" data-testid={`mention-also-question-${idx}`}>also a question</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={listRef} style={style} className="z-[80] max-h-[340px] overflow-y-auto rounded-xl border border-border bg-card py-1 shadow-2xl" data-testid="mention-menu">
      {loading && flat.length === 0 && <p className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</p>}
      {!loading && flat.length === 0 && (
        <p className="px-3 py-3 text-sm text-muted-foreground" data-testid="mention-empty">
          {query ? "Nothing found. Try a reference (matthew 26:62), words from any verse, a question, or a commentary." : "Type a reference (matthew 26:62), words from any Bible verse, a question, or a commentary."}
        </p>
      )}
      <Section title="Scripture" icon={BookOpen} rows={items.scriptures} />
      <Section title="Questions" icon={MessageCircleQuestion} rows={items.questions} />
      <Section title="Commentary" icon={BookMarked} rows={commentary} />
    </div>
  );
});
